import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

/**
 * Global cold-start watchdog.
 *
 * Only triggers when the app is genuinely stuck on its initial loading state
 * (auth `loading === true`) past WATCHDOG_TIMEOUT_MS. If the app boots
 * successfully (loading flips to false) before the timer fires, the watchdog
 * cancels itself — no false-positive reloads on slow networks.
 *
 * Also listens for a `STALE_FALLBACK_SERVED` message from the service worker.
 * If the SW had to serve a cached index.html because the network failed, and
 * we're still in the loading state, we trigger early recovery instead of
 * waiting the full timeout.
 *
 * Recovery clears all caches and hard-reloads with a `?_bust=` param so the
 * SW serves fresh index.html + chunks. Guarded with sessionStorage to
 * prevent reload loops — on a second hang we surface a manual recovery card.
 */
const WATCHDOG_TIMEOUT_MS = 12000;
const RECOVERY_FLAG = "app_boot_recovery";

export const AppBootWatchdog = () => {
  const { loading } = useAuth();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    // App already booted — nothing to watch.
    if (!loading) return;

    const alreadyRecovered = sessionStorage.getItem(RECOVERY_FLAG) === "1";

    const triggerRecovery = (reason: string) => {
      console.warn(`[AppBootWatchdog] Triggering recovery: ${reason}`);
      if (!alreadyRecovered) {
        sessionStorage.setItem(RECOVERY_FLAG, "1");
        clearCachesAndReload();
      } else {
        setStuck(true);
      }
    };

    const timeoutId = setTimeout(() => {
      triggerRecovery("App still on initial loading state after 12s");
    }, WATCHDOG_TIMEOUT_MS);

    // Listen for SW signal that it served a stale cached index.html.
    // If we get this while still loading, the bundle is almost certainly
    // referencing dead chunks — recover immediately instead of waiting.
    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === "STALE_FALLBACK_SERVED") {
        triggerRecovery("Service worker served stale fallback");
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSwMessage);
    }

    return () => {
      clearTimeout(timeoutId);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSwMessage);
      }
    };
  }, [loading]);

  // Clear the recovery flag on a successful boot so future cold starts
  // get a fresh chance at silent recovery.
  useEffect(() => {
    if (!loading) {
      sessionStorage.removeItem(RECOVERY_FLAG);
    }
  }, [loading]);

  if (!stuck) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm p-6">
      <div className="max-w-sm w-full bg-card border border-border rounded-2xl shadow-lg p-6 space-y-4 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertCircle className="w-6 h-6 text-destructive" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-foreground">
            Taking longer than usual
          </h2>
          <p className="text-sm text-muted-foreground">
            We're having trouble loading the app. Tap below to refresh — if it
            keeps happening, try closing and reopening the app.
          </p>
        </div>
        <Button
          onClick={() => {
            sessionStorage.removeItem(RECOVERY_FLAG);
            clearCachesAndReload();
          }}
          className="w-full"
        >
          Refresh app
        </Button>
      </div>
    </div>
  );
};

async function clearCachesAndReload() {
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (err) {
    console.error("[AppBootWatchdog] cache clear failed", err);
  }
  // NOTE: Do NOT purge localStorage auth tokens here. The watchdog must never
  // delete user credentials — it caused logouts mid-login for healthy sessions.
  // If a token is genuinely poisoned, useBiometricAuth handles its own cleanup
  // when refreshSession returns refresh_token_already_used.
  const url =
    window.location.origin +
    window.location.pathname +
    "?_bust=" +
    Date.now();
  window.location.replace(url);
}
